/**
 * Cleo In-Proposal — staff Link A line bind, class-safe copy, trail honesty.
 * Prefill "What is the [Brand Model] for?" still exists; free Q&A is grounded LLM in the planner.
 */
import { formatProductBrandModelReference, resolveProductDisplayName } from "./productDisplayName";
import {
  audioClassFunctionBlurb,
  copyMisidentifiesPreampAsPowerAmp,
  lineLocksAsLinePreamp,
  lockedPreampFunctionBlurbForLine,
  PREAMP_FUNCTION_BLURB,
  PRODUCT_CLASS_GROUNDING_PROMPT,
  sourceHaystackForProductClass,
} from "./proposalCleoProductClass";
import { parseCustomerExplanationStored } from "./proposalItemCleoDescription";

/** Legacy exact string the model was taught to emit. Never return this unless a real PIC trail exists. */
export const CLEO_PROPOSAL_REFUSE_FALLBACK =
  "I'm not certain about that. I've notified your consultant and they will get back to you.";

/** Honest refuse when we have not created a consultant trail. */
export const CLEO_PROPOSAL_UNCERTAIN_NO_TRAIL =
  "I'm not certain about that from this proposal. Ask another way, or we can note it for your consultant when this chat ends.";

/** Honest escalate after a PIC-visible note exists on the proposal (WhatsApp digest still waits for session end). */
export const CLEO_PROPOSAL_NOTED_WITH_TRAIL =
  "I'm not certain about that. I've noted this for your consultant on this proposal, and they will follow up.";

/** Multi-line deictic with no valid bind — do not LLM-guess a line. */
export const CLEO_PROPOSAL_WHICH_LINE_PROMPT =
  "Which item on this proposal do you mean? Name the product, or tap ? on that line.";

/** Answer-time note when stored Cleo copy or snapshot is dropped for class contradiction (targeted regen, not mass rewrite). */
export const CLEO_TARGETED_CLEO_DESCRIPTION_REGEN_NOTE =
  "Stored Cleo description / descriptionSnapshot omitted at answer time because it contradicted locked product class (e.g. Helios snapshot calling a line preamp a power amp). Candidate for targeted regen — not a mass rewrite.";

/**
 * Soon's Grok quality bar with hard length discipline. Style target — never a thesis.
 * Helios: dual-mono Class A line preamp; what / where / why; lineage only if asked.
 */
export const CLEO_STAFF_LINK_A_SALES_STYLE_PROMPT = [
  "HARD LENGTH: this is not a thesis and not a Grok essay. Explain / what-is: 2–5 short sentences, then STOP. Compare: a bit longer only if needed (about 4–8 short sentences) — compressed side-by-side plus one practical summary. Never a second essay paragraph. Never a brochure.",
  "Voice: precise colleague; Grok-useful facts, Cleo-short sales copy.",
  "Explain: what it is, where it sits, why it matters. Helios bar (do not dump): Gryphon Helios is a dual-mono pure Class A LINE PREAMP — never a power amp, never “powers your speakers”.",
  "It is the control/line stage in front of the power amps. Successor to Pandora / below Commander only as a short clause when asked about lineage — never as a pairing or amp recommendation.",
  "At most one standout fact unless asked (dual-chassis + PSU, relay volume, modular DAC/PSU, Nordic Noir). Do not list the brochure.",
  "Compare after a named target (e.g. T+A P 3100 HV): fold topology/philosophy, sound, and key difference into a compressed side-by-side, then one practical buyer summary. Do not write five labeled paragraphs. Do not soft-refuse. Do not rank a winner as fact. Do NOT slam other products. Do not bash. Do not invent specs, measurements, or prices as fact.",
  "If the user did not name current gear, the app asks once for make/model (optionally line-stage/phono/DAC, SE vs balanced) — do not invent a target.",
].join(" ");

/** Short-answer / explain-intent guidance shared with the proposal Q&A system prompt. */
export const CLEO_PROPOSAL_LINE_EXPLAIN_PROMPT_GUIDANCE = [
  CLEO_STAFF_LINK_A_SALES_STYLE_PROMPT,
  "Free questions work — do not wait for magic phrases or an FAQ catalog.",
  "When brand, model, or name is identifiable on that proposal line: you MUST describe the product.",
  "Prefer stored Description / Purpose (cleoDescription and what+why) when present and they do not contradict category/name class.",
  "If those are missing or were dropped for class contradiction, generate a short explanation bound to that line and this proposal using product knowledge plus locked class.",
  `Do NOT respond with "${CLEO_PROPOSAL_REFUSE_FALLBACK}" unless the system has actually created a consultant trail.`,
  "Do NOT say you have notified the consultant if you have not.",
  PRODUCT_CLASS_GROUNDING_PROMPT,
  "Do NOT offer discounts, change scope, or invent a comparison target.",
  "Do NOT slam other products. ONLY recommend products in AUTHORISED CATALOGUE CANDIDATES or already on this proposal. Never mention catalogues, allowlists, or refuse-rails in the spoken answer.",
  "Named pairing / recommend answers may name only retrieved authorised rows ∪ proposal lines. Prefer same-brand match when those hits exist. Otherwise: matching within the same brand is best; customers are also known to use another authorised name. Empty authorised list → class pairing advice only — do not invent SKUs, do not emit a meta refuse line.",
  "Other brands: informative and unbiased. No bash, no “ours is better,” no invented specs or prices. Do not recommend a Clarity substitute unless it is a retrieved authorised row.",
].join(" ");

export type ProposalCleoExplainLine = {
  id?: number | null;
  name?: string | null;
  brand?: string | null;
  model?: string | null;
  displayName?: string | null;
  category?: string | null;
  quantity?: number | null;
  rawText?: string | null;
  descriptionSnapshot?: string | null;
  cleoDescription?: string | null;
  customerExplanation?: string | null;
};

export type ProposalCleoPendingAsk = {
  key: number;
  question: string;
  proposalItemId?: number;
};

function clean(value: string | null | undefined): string {
  return String(value ?? "").replace(/\s+/g, " ").trim();
}

/** Fold typographic apostrophes so "What’s this for?" matches ASCII "What's this for?". */
export function normalizeCleoAskQuestion(question: string): string {
  return clean(question)
    .replace(/[\u2018\u2019\u201B\u2032`]/g, "'")
    .toLowerCase()
    .replace(/[?.!]+$/g, "")
    .trim();
}

const EXPLAIN_QUESTION_STOPWORDS = new Set([
  "what",
  "whats",
  "the",
  "for",
  "does",
  "dos",
  "dose",
  "how",
  "hows",
  "this",
  "that",
  "you",
  "use",
  "using",
  "and",
  "are",
  "is",
  "a",
  "an",
  "in",
  "on",
  "of",
  "to",
  "do",
  "it",
  "item",
  "line",
  "product",
  "compare",
  "compared",
  "comparing",
  "versus",
  "better",
  "worse",
  "than",
  "with",
  "against",
  "which",
  "about",
  "from",
  "would",
  "could",
  "should",
  "match",
  "matches",
  "matching",
  "pair",
  "pairs",
  "paired",
  "pairing",
  "recommend",
  "recommended",
  "recommendation",
  "recommendations",
  "suggest",
  "suggested",
  "suggestion",
  "goes",
  "works",
  "suitable",
  "complement",
  "need",
  "needs",
]);

/** Class words that must not uniquely bind a deictic question to a wrong line. */
const GENERIC_CLASS_TOKENS = new Set([
  "amp",
  "amps",
  "amplifier",
  "amplifiers",
  "power",
  "preamp",
  "preamps",
  "preamplifier",
  "preamplifiers",
  "speaker",
  "speakers",
  "cable",
  "cables",
  "gear",
  "system",
]);

export type ProposalLineBindKind = "bound" | "ask_which" | "none";

export type ProposalLineBindResult =
  | { kind: "bound"; line: ProposalCleoExplainLine }
  | { kind: "ask_which" }
  | { kind: "none" };

/** Best available staff/customer line label for the prefilled Cleo question. */
export function resolveProposalLineAskCleoLabel(line: {
  brand?: string | null;
  model?: string | null;
  name?: string | null;
  displayName?: string | null;
  title?: string | null;
  descriptionSnapshot?: string | null;
}): string {
  const brandModel = formatProductBrandModelReference(line);
  if (brandModel) return brandModel;
  const display = resolveProductDisplayName(line);
  if (display && display !== "Product") return display;
  const title = clean(line.title);
  if (title) return title;
  const name = clean(line.name);
  if (name) return name;
  const snapshot = clean(line.descriptionSnapshot).split("\n")[0] ?? "";
  if (snapshot) return snapshot;
  return "this item";
}

export function buildProposalLineExplainQuestion(label: string): string {
  const trimmed = clean(label) || "this item";
  return `What is the ${trimmed} for?`;
}

/**
 * Free deictic explain: "what is this/that", "what's this", "what is this for",
 * "whats this for", "what is this item", "what is this amp?" — same staff intent as the line `?` prefill.
 */
export function isDeicticExplainQuestion(question: string): boolean {
  const q = normalizeCleoAskQuestion(question);
  if (!q) return false;
  return /^what(?:'s|s| is) (?:this|that)(?:\s+used)?(?:\s+for)?(?:\s+[\w'-]{1,24})?$/.test(q);
}

export function isProposalLineExplainQuestion(question: string): boolean {
  const q = normalizeCleoAskQuestion(question);
  if (!q) return false;
  if (isDeicticExplainQuestion(question)) return true;
  if (/^what is the .+ for$/.test(q)) return true;
  if (/^what(?:'s|s| is) (?:the )?.+$/.test(q) && /\bfor\b/.test(q)) return true;
  if (/^what does (?:the )?.+ do$/.test(q)) return true;
  return false;
}

/**
 * Class-identity questions: what this is / what it is for. Locked preamps must
 * answer these deterministically — do not LLM-primary.
 */
export function isClassIdentityExplainQuestion(question: string): boolean {
  if (isDeicticExplainQuestion(question)) return true;
  if (isProposalLineExplainQuestion(question)) return true;
  const q = normalizeCleoAskQuestion(question);
  if (!q) return false;
  if (/\bcompare\b/.test(q)) return false;
  // Pairing / "what amp matches" is not class identity. "what amp is Helios" still is.
  const whatAmpIsClass =
    /\b(?:what|which)\s+(?:kind of\s+|type of\s+)?(?:power\s+|integrated\s+)?(?:amp|amps|amplifier|amplifiers)\s+(?:is|are)\b/.test(
      q,
    );
  if (whatAmpIsClass) return true;
  if (
    /\b(?:match(?:es|ing)?|pair(?:s|ed|ing)?|recommend(?:ed|ation|s)?|suggest(?:ed|ion|s)?|(?:goes|works)\s+with)\b/.test(
      q,
    )
  ) {
    return false;
  }
  if (/\b(?:what|which)\s+(?:power\s+|integrated\s+)?(?:amp|amps|amplifier|amplifiers)\b/.test(q)) {
    return false;
  }
  // "what is Helios" / "what is the Gryphon Helios" (no "for" required)
  if (/^what(?:'s|s| is) (?:the )?.{1,80}$/.test(q) && !/\b(price|cost|discount|warranty|delivery|when|install)\b/.test(q)) {
    return true;
  }
  return false;
}

export function isCleoProposalRefuseFallback(text: string): boolean {
  return clean(text) === CLEO_PROPOSAL_REFUSE_FALLBACK;
}

/** True when copy claims a consultant was already notified or a trail was already written. */
export function claimsConsultantWasNotified(text: string): boolean {
  const t = clean(text).toLowerCase();
  if (!t) return false;
  if (t === clean(CLEO_PROPOSAL_NOTED_WITH_TRAIL).toLowerCase()) return true;
  if (t === clean(CLEO_PROPOSAL_REFUSE_FALLBACK).toLowerCase()) return true;
  if (/notified (?:your |the )?consultant/.test(t)) return true;
  if (/i(?:'ve| have) noted this for your consultant/.test(t)) return true;
  if (/consultant and they will (?:get back|follow up)/.test(t)) return true;
  return false;
}

/**
 * Never emit noted-with-trail (or paraphrased notify) unless persistCompareEscalationTrail ran.
 */
export function rewriteConsultantNotifyIfNoTrail(text: string, trailPersisted: boolean): string {
  const raw = clean(text);
  if (trailPersisted) {
    if (!raw || isCleoProposalRefuseFallback(raw)) return CLEO_PROPOSAL_NOTED_WITH_TRAIL;
    return raw;
  }
  if (!raw) return CLEO_PROPOSAL_UNCERTAIN_NO_TRAIL;
  if (raw === CLEO_PROPOSAL_NOTED_WITH_TRAIL || isCleoProposalRefuseFallback(raw) || claimsConsultantWasNotified(raw)) {
    return CLEO_PROPOSAL_UNCERTAIN_NO_TRAIL;
  }
  return raw;
}

export function proposalLineHasIdentifiableProduct(line: ProposalCleoExplainLine): boolean {
  const brand = clean(line.brand);
  const model = clean(line.model);
  if (brand && model) return true;
  if (brand.length >= 2 && model.length >= 1) return true;
  const label = resolveProposalLineAskCleoLabel(line);
  if (!label || /^this item$/i.test(label)) return false;
  return label.replace(/[^a-z0-9]+/gi, "").length >= 3;
}

function tokenizeExplainQuery(question: string): string[] {
  return Array.from(
    new Set(
      clean(question)
        .toLowerCase()
        .split(/[^a-z0-9]+/g)
        .filter((token) => token.length >= 3 && !EXPLAIN_QUESTION_STOPWORDS.has(token)),
    ),
  );
}

function identityTokens(question: string): string[] {
  return tokenizeExplainQuery(question).filter((token) => !GENERIC_CLASS_TOKENS.has(token));
}

function lineHaystack(line: ProposalCleoExplainLine): string {
  return [
    line.name,
    line.brand,
    line.model,
    line.displayName,
    line.category,
    line.descriptionSnapshot,
    line.rawText,
    line.cleoDescription,
    line.customerExplanation,
  ]
    .map((value) => clean(String(value ?? "")).toLowerCase())
    .filter(Boolean)
    .join(" ");
}

function scoreLineAgainstTokens(item: ProposalCleoExplainLine, tokens: string[], qNorm: string): number {
  const hay = lineHaystack(item);
  const label = resolveProposalLineAskCleoLabel(item).toLowerCase();
  if (!hay && !label) return 0;
  let score = 0;
  for (const token of tokens) {
    if (hay.includes(token) || label.includes(token)) score += token.length >= 5 ? 2 : 1;
  }
  if (label && (qNorm.includes(label) || hay.includes(qNorm))) score += 4;
  return score;
}

function boundLineFromId(
  items: ProposalCleoExplainLine[],
  proposalItemId?: number | null,
): ProposalCleoExplainLine | null {
  const boundId = Number(proposalItemId ?? 0);
  if (!Number.isFinite(boundId) || boundId <= 0) return null;
  return items.find((item) => Number(item.id) === boundId) ?? null;
}

/**
 * Bind a question to a proposal line.
 * Named product tokens always win over sticky / session `proposalItemId`.
 * Deictic + exactly one identifiable line auto-binds. Multi-line deictic with
 * no valid bind asks which item — never token-score "this" into a wrong line.
 */
export function resolveProposalLineExplainBind(
  question: string,
  items: ProposalCleoExplainLine[],
  opts?: { proposalItemId?: number | null },
): ProposalLineBindResult {
  const identifiable = items.filter(proposalLineHasIdentifiableProduct);
  const boundFromId = boundLineFromId(items, opts?.proposalItemId);
  const tokens = identityTokens(question);
  const qNorm = clean(question).toLowerCase();
  const deictic = isDeicticExplainQuestion(question);

  if (tokens.length > 0) {
    let best: { score: number; item: ProposalCleoExplainLine } | null = null;
    let second = 0;
    for (const item of items) {
      const score = scoreLineAgainstTokens(item, tokens, qNorm);
      if (!best || score > best.score) {
        second = best?.score ?? 0;
        best = { score, item };
      } else if (score === best.score) {
        second = score;
      } else if (score > second) {
        second = score;
      }
    }
    if (best && best.score > 0 && best.score > second) {
      return { kind: "bound", line: best.item };
    }
    if (deictic && identifiable.length > 1 && !(boundFromId && proposalLineHasIdentifiableProduct(boundFromId))) {
      return { kind: "ask_which" };
    }
    return { kind: "none" };
  }

  if (boundFromId && proposalLineHasIdentifiableProduct(boundFromId)) {
    return { kind: "bound", line: boundFromId };
  }

  const mayAutoBind = deictic || isProposalLineExplainQuestion(question);
  if (mayAutoBind && identifiable.length === 1) {
    return { kind: "bound", line: identifiable[0] };
  }
  if (deictic && identifiable.length > 1) {
    return { kind: "ask_which" };
  }
  return { kind: "none" };
}

export function matchProposalLineForExplainQuestion(
  question: string,
  items: ProposalCleoExplainLine[],
  opts?: { proposalItemId?: number | null },
): ProposalCleoExplainLine | null {
  const result = resolveProposalLineExplainBind(question, items, opts);
  return result.kind === "bound" ? result.line : null;
}

/** Send sticky lastItemId only for deictic / no-name questions, not for a later named product. */
export function shouldAttachStickyProposalItemId(question: string): boolean {
  return identityTokens(question).length === 0;
}

export function resolveAskCleoProposalItemId(opts: {
  question: string;
  explicitProposalItemId?: number | null;
  stickyProposalItemId?: number | null;
}): number | undefined {
  const explicit = Number(opts.explicitProposalItemId ?? 0);
  if (Number.isFinite(explicit) && explicit > 0) return explicit;
  if (!shouldAttachStickyProposalItemId(opts.question)) return undefined;
  const sticky = Number(opts.stickyProposalItemId ?? 0);
  if (Number.isFinite(sticky) && sticky > 0) return sticky;
  return undefined;
}

export type SanitizedProposalLineCopy = {
  cleoDescription: string;
  customerExplanation: string;
  descriptionSnapshot: string;
  droppedClassContradictingCopy: boolean;
  targetedRegenNote: string | null;
};

/** Strip stored copy that contradicts locked preamp identity before it reaches the LLM. */
export function sanitizeProposalLineCopyForLlm(line: ProposalCleoExplainLine): SanitizedProposalLineCopy {
  let cleoDescription = clean(line.cleoDescription);
  let customerExplanation = clean(line.customerExplanation);
  let descriptionSnapshot = clean(line.descriptionSnapshot);
  let droppedClassContradictingCopy = false;
  if (identityIndicatesPreamp(line)) {
    if (copyMisidentifiesPreampAsPowerAmp(cleoDescription)) {
      cleoDescription = "";
      droppedClassContradictingCopy = true;
    }
    if (copyMisidentifiesPreampAsPowerAmp(customerExplanation)) {
      customerExplanation = "";
      droppedClassContradictingCopy = true;
    }
    if (copyMisidentifiesPreampAsPowerAmp(descriptionSnapshot)) {
      descriptionSnapshot = "";
      droppedClassContradictingCopy = true;
    }
  }
  return {
    cleoDescription,
    customerExplanation,
    descriptionSnapshot,
    droppedClassContradictingCopy,
    targetedRegenNote: droppedClassContradictingCopy ? CLEO_TARGETED_CLEO_DESCRIPTION_REGEN_NOTE : null,
  };
}

/**
 * Answer-time class lock for LLM output. If the model calls a locked preamp a power amp
 * (or claims it drives speakers), replace with the composed class-safe fallback.
 */
export function filterStaffLinkALlmAnswerAgainstLockedClass(
  text: string,
  opts: {
    boundLine?: ProposalCleoExplainLine | null;
    composedFallback?: string | null;
    /** Pairing answers mention complementary power amps — only lock sentences about the bound preamp. */
    scopeClassLockToBoundLine?: boolean;
  },
): string {
  const raw = clean(text);
  const fallback = clean(opts.composedFallback ?? "");
  const line = opts.boundLine ?? null;
  const lockedPreamp = Boolean(line && identityIndicatesPreamp(line));
  const wholeAnswerUnsafe =
    copyMisidentifiesPreampAsPowerAmp(raw) ||
    /\bpower(?:[\s-]?amplifiers?|[\s-]?amps?)\b.{0,48}\bdrives?\b/i.test(raw) ||
    /\bdrives? (?:your |the )?(?:loud)?speakers\b/i.test(raw);
  const classUnsafe = opts.scopeClassLockToBoundLine
    ? boundPreampClassUnsafeInAnswer(raw, line)
    : wholeAnswerUnsafe;
  if (line && lockedPreamp && classUnsafe) {
    if (fallback) return fallback;
    return composeProposalLineExplainAnswer(line, {
      ruleExplanation: ruleExplanationForProposalLine(line),
    });
  }
  if (fallback && (isCleoProposalRefuseFallback(raw) || raw === CLEO_PROPOSAL_UNCERTAIN_NO_TRAIL || !raw)) {
    return fallback;
  }
  return raw || CLEO_PROPOSAL_UNCERTAIN_NO_TRAIL;
}

function boundPreampClassUnsafeInAnswer(
  text: string,
  line: ProposalCleoExplainLine | null,
): boolean {
  if (!line) return false;
  const needles = [line.model, line.displayName, line.name]
    .map((value) => clean(String(value ?? "")).toLowerCase())
    .filter((value) => value.length >= 3);
  const sentences = clean(text).split(/(?<=[.!?])\s+/);
  const relevant = sentences.filter((sentence) => {
    const hay = sentence.toLowerCase();
    if (needles.some((needle) => hay.includes(needle))) return true;
    return /\b(?:this (?:item|preamp|preamplifier|unit|line)|the preamp)\b/.test(hay);
  });
  const check = relevant.length ? relevant : [];
  return check.some((sentence) => {
    const t = sentence.toLowerCase();
    if (/\bis(?: itself)? (?:a |an )?(?:high-end |high end |premium )?power(?:[\s-]?amp)/.test(t)) return true;
    if (/\bpowers? your speakers\b/.test(t) && !/\bpower amp/.test(t)) return true;
    if (/\bdrives? (?:your |the )?(?:loud)?speakers\b/.test(t) && !/\bpower amp/.test(t)) return true;
    return copyMisidentifiesPreampAsPowerAmp(sentence);
  });
}

export function ruleExplanationForProposalLine(line: ProposalCleoExplainLine): string | null {
  const locked = lockedPreampFunctionBlurbForLine({
    category: line.category,
    name: line.name,
    brand: line.brand,
    model: line.model,
    displayName: line.displayName,
    rawText: line.rawText,
    descriptionSnapshot: line.descriptionSnapshot,
  });
  if (locked) return locked;
  const safe = sanitizeProposalLineCopyForLlm(line);
  const hay = sourceHaystackForProductClass({
    category: line.category,
    name: line.name,
    brand: line.brand,
    model: line.model,
    displayName: line.displayName,
    rawText: line.rawText,
    descriptionSnapshot: safe.descriptionSnapshot,
  });
  return explainProposalLineFromRules(hay);
}

export function explainProposalLineFromRules(haystack: string): string | null {
  const audio = audioClassFunctionBlurb(haystack);
  if (audio) return audio;
  const h = String(haystack ?? "");
  if (/(wifi|wi-fi|wireless|access point|\bap\b|mesh)/i.test(h)) {
    return "This helps provide stable wireless internet coverage in the spaces included in your proposal.";
  }
  if (/(network switch|ethernet switch|poe switch|\bswitch\b)/i.test(h)) {
    return "This connects multiple network devices together so they can communicate reliably.";
  }
  if (/(router|gateway|modem|ont)/i.test(h)) {
    return "This is the main internet gateway that links your home network to your internet service.";
  }
  if (/(speaker|subwoofer|bookshelf|floorstand|in-wall|in-ceiling)/i.test(h)) {
    return "This is an audio output component that produces sound in the room.";
  }
  if (/(projector|screen|oled|tv|display)/i.test(h)) {
    return "This handles video display so you can view content clearly at the intended size.";
  }
  if (/(camera|cctv|nvr|dvr|surveillance)/i.test(h)) {
    return "This is part of the monitoring system for security coverage in the specified area.";
  }
  if (/(intercom|door station|doorbell|gate)/i.test(h)) {
    return "This supports visitor communication and controlled entry at the door or gate.";
  }
  if (/(lighting|dimmer|scene keypad|lutron|knx)/i.test(h)) {
    return "This is used to control lighting levels and scenes for daily use.";
  }
  return null;
}

function compactSentences(parts: string[], maxSentences = 4): string {
  const sentences: string[] = [];
  for (const part of parts) {
    const text = clean(part);
    if (!text) continue;
    const split = text
      .split(/(?<=[.!?])\s+/)
      .map((s) => s.trim())
      .filter(Boolean);
    for (const sentence of split.length > 0 ? split : [text]) {
      if (sentences.some((existing) => existing.toLowerCase() === sentence.toLowerCase())) continue;
      sentences.push(sentence);
      if (sentences.length >= maxSentences) {
        return sentences.join(" ");
      }
    }
  }
  return sentences.join(" ");
}

function identityIndicatesPreamp(line: ProposalCleoExplainLine): boolean {
  return lineLocksAsLinePreamp({
    category: line.category,
    name: line.name,
    brand: line.brand,
    model: line.model,
    displayName: line.displayName,
    rawText: line.rawText,
    descriptionSnapshot: line.descriptionSnapshot,
  });
}

/** Prefer stored Cleo what+why copy when present and it does not contradict known class. */
export function composeProposalLineExplainFromStored(line: ProposalCleoExplainLine): string | null {
  const description = clean(line.cleoDescription);
  const hover = parseCustomerExplanationStored(line.customerExplanation);
  const parts: string[] = [];
  if (description) parts.push(description);
  if (hover?.whatThisIs && hover.whatThisIs.toLowerCase() !== description.toLowerCase()) {
    parts.push(hover.whatThisIs);
  }
  if (hover?.whyYouNeedIt) parts.push(hover.whyYouNeedIt);
  const composed = compactSentences(parts, 4);
  if (!composed) return null;
  if (identityIndicatesPreamp(line) && copyMisidentifiesPreampAsPowerAmp(composed)) {
    return null;
  }
  return composed;
}

/**
 * Last-resort short description when identity exists but stored copy and LLM are unavailable.
 * Must not use the consultant-refuse path.
 */
export function composeProposalLineExplainIdentityFallback(line: ProposalCleoExplainLine): string {
  const label = resolveProposalLineAskCleoLabel(line);
  if (
    lineLocksAsLinePreamp({
      category: line.category,
      name: line.name,
      brand: line.brand,
      model: line.model,
      displayName: line.displayName,
      rawText: line.rawText,
      descriptionSnapshot: line.descriptionSnapshot,
    })
  ) {
    return compactSentences([`${label} is included in this proposal.`, PREAMP_FUNCTION_BLURB], 4);
  }
  const category = clean(line.category);
  if (category) {
    return `${label} is included in this proposal. It is the ${category} specified for this system — the piece you use for that role in the design.`;
  }
  return `${label} is included in this proposal. It is the specified component for this system's design, used as shown on this line.`;
}

export function composeProposalLineExplainAnswer(
  line: ProposalCleoExplainLine,
  opts?: { ruleExplanation?: string | null },
): string {
  const stored = composeProposalLineExplainFromStored(line);
  if (stored) return stored;
  const rule = clean(opts?.ruleExplanation ?? "");
  if (rule) {
    const label = resolveProposalLineAskCleoLabel(line);
    return compactSentences([`${label} is included in this proposal.`, rule], 4);
  }
  return composeProposalLineExplainIdentityFallback(line);
}

/**
 * Last-resort named compare when the LLM is unavailable.
 * Compressed side-by-side + practical summary — no essay, no ranking-as-fact.
 */
export function composeNamedCompareAnswer(
  line: ProposalCleoExplainLine,
  compareTarget: string,
  _opts?: { ruleExplanation?: string | null },
): string {
  const label = resolveProposalLineAskCleoLabel(line);
  const target = clean(compareTarget) || "that product";
  const category = clean(line.category);
  const preampLocked = lineLocksAsLinePreamp({
    category: line.category,
    name: line.name,
    brand: line.brand,
    model: line.model,
    displayName: line.displayName,
    rawText: line.rawText,
    descriptionSnapshot: line.descriptionSnapshot,
  });
  const classBit = preampLocked
    ? `${label} is a line preamp on this proposal`
    : category
      ? `${label} is locked on this proposal as ${category}`
      : `${label} is the focus line on this proposal`;
  return `Side by side — ${label} vs ${target}. ${classBit} (line-stage / control, not speaker drive). Topology and sound: I will not invent voicing or specs for ${target}. Practical summary: same role unless you said otherwise — walk taste with your consultant, not a scoreboard.`;
}
